import * as React from 'react';

import { enumFacilitySortOption } from '@rematter/frontend-lib-api-client';
import { FacilityRecord } from '@rematter/modules/facilities/types/facility';
import {
  Badge,
  Button,
  DataGrid,
  FloatingToolbar,
  ListScreenLayout,
  Pagination,
  type PaginationProps,
  SearchField,
  Separator,
  useDataGridSelection,
} from '@rematter/ui-web';
import { useAtom } from 'jotai';
import { LucidePlusCircle, LucideTrash } from 'lucide-react';

import { useDeleteFacilityMutation } from '../../hooks/mutations/useDeleteFacilityMutation';
import { useFacilitiesQuery } from '../../hooks/useFacilitiesQuery';

import { FacilityDialog } from './FacilityDialog';
import { useFacilitiesColumns } from './hooks/useFacilitiesColumns';

import { useTranslations } from '@/facilities/i18n/useTranslations';
import {
  $limit,
  $offset,
  $searchTerm,
} from '~/modules/facilities/_state/facilitiesList.state';
import useDebouncedValue from '~/shared/hooks/useDebounce';

//
// ⚛️ ⚛️ ⚛️ FACILITIES LIST VIEW ⚛️ ⚛️ ⚛️
//
export const FacilitiesListView = () => {
  const t = useTranslations();

  // States
  const [selectedRows, setSelectedRows] = React.useState<FacilityRecord[]>([]);
  const [searchTerm, setSearchTerm] = useAtom($searchTerm);
  const [offset, setOffset] = useAtom($offset);
  const [limit, setLimit] = useAtom($limit);
  const [dialogState, setDialogState] = React.useState<{
    isOpen: boolean;
    facility?: FacilityRecord;
  }>({
    isOpen: false,
  });

  // Other data hooks
  const debouncedSearchTerm = useDebouncedValue(searchTerm ?? '', 500);

  const {
    data: { facilities = [], total = 0 } = {
      facilities: [],
      total: 0,
    },
    error,
    refetch,
    isLoading,
  } = useFacilitiesQuery({
    searchTerm: debouncedSearchTerm,
    limit,
    offset,
    sorting: [enumFacilitySortOption.NameAsc],
  });

  const handleSelectionChange = useDataGridSelection(
    facilities,
    setSelectedRows,
  );

  const { mutateAsync: deleteFacility, isPending: isDeleting } =
    useDeleteFacilityMutation();

  // Memoized values
  const emptyPanelProps = React.useMemo(
    () => ({
      title: 'No facilities',
      description: <span>Create a facility to get started.</span>,
      centered: true,
    }),
    [],
  );

  // Callbacks
  const handlePaginationChange = React.useCallback<
    NonNullable<PaginationProps['onChange']>
  >(({ limit: newLimit, offset: newOffset }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  }, []);
  const handleSearch = React.useCallback((value: string) => {
    setSearchTerm(value || undefined);
    setOffset(0);
  }, []);
  const handleEdit = React.useCallback((facility: FacilityRecord) => {
    setDialogState({
      isOpen: true,
      facility,
    });
  }, []);
  const handleCreate = React.useCallback(() => {
    setDialogState({
      isOpen: true,
    });
  }, []);
  const handleCloseDialog = React.useCallback(() => {
    setDialogState({
      isOpen: false,
    });
  }, []);
  const handleDelete = React.useCallback(async () => {
    try {
      await Promise.all(
        selectedRows.map((row) => deleteFacility({ id: row.id })),
      );
      setSelectedRows([]);
      refetch();
    } catch (err) {
      console.error('Failed to delete facilities:', err);
    }
  }, [selectedRows, deleteFacility, refetch]);

  const columns = useFacilitiesColumns({ onEdit: handleEdit });

  return (
    <>
      <ListScreenLayout
        className="@FacilitiesListView"
        filters={
          <SearchField
            autoFocus
            value={searchTerm ?? ''}
            onChange={handleSearch}
            placeholder={t.facilities.list.search.placeholder}
            className="w-64"
          />
        }
        actions={
          <Button
            onPress={handleCreate}
            variant="solid"
            color="primary"
            icon={<LucidePlusCircle />}
          >
            {t.facilities.list.actions.create}
          </Button>
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<FacilityRecord>
          isLoading={isLoading}
          columns={columns}
          dataSource={facilities}
          rowKey="id"
          selectionMode="multiple"
          onSelectionChange={handleSelectionChange}
          aria-label={t.facilities.list.aria}
          fullWidth
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.facilities.errors.loadingFacilities(
                    error.message,
                  ),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
          className="w-full text-sm"
        />
      </ListScreenLayout>

      <FloatingToolbar visible={selectedRows.length > 0}>
        <span className="text-sm">
          <Badge color="primary" size="small">
            {selectedRows.length}
          </Badge>{' '}
          {t.facilities.list.selected(selectedRows.length)}
        </span>
        <Separator hideDivider />
        <Button
          variant="solid"
          color="failure"
          icon={<LucideTrash />}
          isDisabled={selectedRows.length < 1 || isDeleting}
          isLoading={isDeleting}
          onPress={handleDelete}
        >
          {t.facilities.list.actions.delete(selectedRows.length)}
        </Button>
      </FloatingToolbar>

      <FacilityDialog
        isOpen={dialogState.isOpen}
        onClose={handleCloseDialog}
        facility={dialogState.facility}
        onSuccess={() => refetch()}
      />
    </>
  );
};
